import React, { useState, useRef, useEffect, useCallback } from "react"
import ColorPicker from "aldoo-ra/HtmlEditor/StyleClassEditor/components/ColorPicker"

// Approximate rendered height of the ColorPicker panel (px)
const PICKER_HEIGHT = 340

export default function ColorPickerEditor({
  onChange,
  value,
  field,
  readOnly = false,
}) {
  const [open, setOpen] = useState(false)
  const [openAbove, setOpenAbove] = useState(false)
  const [pendingColor, setPendingColor] = useState(value || "")
  const containerRef = useRef(null)
  const buttonRef = useRef(null)

  useEffect(() => {
    setPendingColor(value || "")
  }, [value])

  const checkPosition = useCallback(() => {
    if (!buttonRef.current) return
    const rect = buttonRef.current.getBoundingClientRect()
    const spaceBelow = window.innerHeight - rect.bottom
    setOpenAbove(spaceBelow < PICKER_HEIGHT)
  }, [])

  useEffect(() => {
    if (!open) return
    checkPosition()
    const onPointerDown = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false)
      }
    }
    document.addEventListener("pointerdown", onPointerDown)
    return () => document.removeEventListener("pointerdown", onPointerDown)
  }, [open, checkPosition])

  const handleToggle = () => {
    if (readOnly) return
    setOpen((o) => !o)
  }

  const handleSelect = (color) => {
    onChange?.(color)
    setOpen(false)
  }

  const handleCancel = () => {
    setPendingColor(value || "")
    setOpen(false)
  }

  const dropdownPositionClass = openAbove
    ? "absolute z-50 bottom-full mb-1 left-0"
    : "absolute z-50 top-full mt-1 left-0"

  return (
    <div ref={containerRef} className="relative inline-block">
      <button
        ref={buttonRef}
        type="button"
        disabled={readOnly}
        onClick={handleToggle}
        className="flex items-center gap-2 px-3 py-2 rounded-lg border border-admin_bordered bg-admin_canvas hover:border-admin_primary/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <span
          className="w-5 h-5 rounded border border-black/20 flex-shrink-0"
          style={{ backgroundColor: pendingColor || "#000000" }}
        />
        <span className="text-admin_text text-sm font-mono">
          {pendingColor || "—"}
        </span>
      </button>

      {open && (
        <div className={dropdownPositionClass}>
          <ColorPicker
            initialColor={pendingColor || "#000000"}
            alpha={false}
            showFormatToggle={true}
            showSwatches={true}
            onChange={setPendingColor}
            onColorSelect={handleSelect}
            onCancel={handleCancel}
            onClose={handleCancel}
          />
        </div>
      )}
    </div>
  )
}
